import { useMemo, useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import StatusBadge from '@/components/base/StatusBadge';
import EmptyState from '@/components/base/EmptyState';
import RowActions from '@/components/base/RowActions';
import { purchaseMaterialStatuses, purchasePaymentStatuses } from '@/types';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

export default function Purchases() {
  const { openCreate, openEdit, openDelete } = useApp();
  const { purchases, projectName } = useData();
  const [query, setQuery] = useState('');
  const [materialStatus, setMaterialStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return purchases.filter((p) => {
      const matchesQuery = !q || p.material.toLowerCase().includes(q) || p.supplier.toLowerCase().includes(q) || projectName(p.projectId).toLowerCase().includes(q);
      const matchesMaterial = !materialStatus || p.materialStatus === materialStatus;
      const matchesPayment = !paymentStatus || p.paymentStatus === paymentStatus;
      return matchesQuery && matchesMaterial && matchesPayment;
    });
  }, [query, materialStatus, paymentStatus, purchases, projectName]);

  const totalValue = purchases.reduce((s, p) => s + p.total, 0);
  const pendingCount = purchases.filter((p) => !['Teslim Alındı', 'İptal'].includes(p.materialStatus)).length;
  const unpaidTotal = purchases.filter((p) => p.paymentStatus !== 'Ödendi').reduce((s, p) => s + p.total, 0);

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader
        title="Satın Alma"
        description={`${filtered.length} satın alma kaydı`}
        actions={
          <button type="button" onClick={() => openCreate('purchase')} className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600">
            <i className="ri-add-line text-lg" /> Yeni Satın Alma
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Toplam Satın Alma</p>
          <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">{formatTL(totalValue)}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Bekleyen Kalem</p>
          <p className="mt-2 font-heading text-2xl font-semibold text-accent-700">{pendingCount} kalem</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Ödenmemiş Tutar</p>
          <p className="mt-2 font-heading text-2xl font-semibold text-accent-700">{formatTL(unpaidTotal)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Malzeme, tedarikçi, proje ara..." className="h-9 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none" />
        </div>
        <select value={materialStatus} onChange={(e) => setMaterialStatus(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Malzeme Durumları</option>
          {purchaseMaterialStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Ödeme Durumları</option>
          {purchasePaymentStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Satın alma kaydı bulunamadı" description="Filtreleri değiştirerek tekrar deneyin." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Proje</th>
                  <th className="px-4 py-3">Tedarikçi</th>
                  <th className="px-4 py-3">Malzeme</th>
                  <th className="px-4 py-3">Marka / Model</th>
                  <th className="px-4 py-3 text-center">Adet</th>
                  <th className="px-4 py-3 text-right">Birim</th>
                  <th className="px-4 py-3 text-right">Toplam</th>
                  <th className="px-4 py-3">Beklenen Teslim</th>
                  <th className="px-4 py-3">Ödeme</th>
                  <th className="px-4 py-3">Malzeme Durumu</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60">
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-foreground-900">{projectName(p.projectId)}</p>
                      <p className="text-xs text-foreground-500">{p.projectId}</p>
                    </td>
                    <td className="px-4 py-3.5 text-foreground-700">{p.supplier}</td>
                    <td className="px-4 py-3.5 font-medium text-foreground-900">{p.material}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{p.brandModel}</td>
                    <td className="px-4 py-3.5 text-center text-foreground-700">{p.quantity}</td>
                    <td className="px-4 py-3.5 text-right text-foreground-700">{formatTL(p.unitPrice)}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{formatTL(p.total)}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{formatDate(p.expectedDelivery)}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={p.paymentStatus} /></td>
                    <td className="px-4 py-3.5"><StatusBadge status={p.materialStatus} /></td>
                    <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('purchase', p)} onDelete={() => openDelete('purchase', p.id, p.material)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}